import React, { useState, useEffect } from "react";
import axiosInstance from "../axiosInstance";

const AdminFinancialEntry = () => {
    const [teachers, setTeachers] = useState([]);
    const [students, setStudents] = useState([]);
    const [receivables, setReceivables] = useState([]);
    const [status, setStatus] = useState("");

    /* ============================
       Parent Payment (AR)
    ============================ */
    const [payment, setPayment] = useState({
        date: "",
        parent: "",
        amount: "",
        paymentMethod: ""
    });

    /* ============================
       Payroll
    ============================ */
    const [payroll, setPayroll] = useState({
        date: "",
        teacher: "",
        hours: "",
        totalPay: ""
    });

    useEffect(() => {
        axiosInstance.get("/teachers").then(res => setTeachers(res.data));
        axiosInstance.get("/students").then(res => setStudents(res.data));
        axiosInstance.get("/receivables").then(res => setReceivables(res.data));
    }, []);

    /* ============================
       Derived Data
    ============================ */
    const parents = [...new Set(students.map(s => s.parent))];

    const parentBalance = receivables
        .filter(r => r.parent === payment.parent)
        .reduce((sum, r) => sum + r.amount, 0);

    /* ============================
       Submit Parent Payment
    ============================ */
    const submitPayment = async () => {
        if (!payment.parent || !payment.amount || !payment.date || !payment.paymentMethod) {
            setStatus("❌ All fields are required");
            return;
        }

        const rawAmount = Math.abs(Number(payment.amount));

        if (rawAmount <= 0) {
            setStatus("❌ Payment must be greater than 0");
            return;
        }

        // Cap payment to outstanding balance
        const appliedAmount = Math.min(rawAmount, rawAmount);

        if (appliedAmount === 0) {
            setStatus("ℹ️ This parent has no outstanding balance");
            return;
        }

        try {
            await axiosInstance.post("/admin/receivable", {
                date: payment.date,
                parent: payment.parent,
                amount: -appliedAmount, // reduce AR, never below 0
                type: payment.paymentMethod === "cash" ? "Cash" : "eTransfer"
            });

            setStatus(
                appliedAmount < rawAmount
                    ? `✅ Payment recorded. Applied $${appliedAmount.toFixed(2)} (excess ignored)`
                    : "✅ Payment recorded"
            );

            setPayment({ date: "", parent: "", amount: "", paymentMethod: "" });

            // Refresh balances
            const res = await axiosInstance.get("/receivables");
            setReceivables(res.data);
        } catch {
            setStatus("❌ Failed to record payment");
        }
    };


    /* ============================
       Submit Payroll
    ============================ */
    const submitPayroll = async () => {
        if (!payroll.date || !payroll.teacher || !payroll.totalPay) {
            setStatus("❌ Missing payroll fields");
            return;
        }

        try {
            await axiosInstance.post("/admin/payroll", payroll);
            setStatus("✅ Payroll payment recorded");
            setPayroll({ date: "", teacher: "", hours: "", totalPay: "" });
        } catch {
            setStatus("❌ Failed to record payroll");
        }
    };

    return (
        <div className="page-container">
            <h2>Admin Financial Entries</h2>

            {/* ========================= */}
            {/* Parent Payments */}
            {/* ========================= */}
            <h3>Record Parent Payment</h3>

            <div className="vertical-form">
                <input
                    type="date"
                    value={payment.date}
                    onChange={e => setPayment({ ...payment, date: e.target.value })}
                />

                <select
                    value={payment.parent}
                    onChange={e => setPayment({ ...payment, parent: e.target.value })}
                >
                    <option value="">Parent</option>
                    {parents.map(p => (
                        <option key={p} value={p}>{p}</option>
                    ))}
                </select>

                {payment.parent && (
                    <p>
                        <strong>Outstanding Balance:</strong>{" "}
                        ${parentBalance.toFixed(2)}
                    </p>
                )}

                <input
                    type="number"
                    step="0.01"
                    placeholder="Payment Amount"
                    value={payment.amount}
                    onChange={e => setPayment({ ...payment, amount: e.target.value })}
                />

                <select
                    value={payment.paymentMethod}
                    onChange={e => setPayment({ ...payment, paymentMethod: e.target.value })}
                >
                    <option value="">Payment Method</option>
                    <option value="cash">Cash</option>
                    <option value="etransfer">eTransfer</option>
                </select>

                <button className="submit-btn" onClick={submitPayment}>
                    Record Payment
                </button>
            </div>

            <hr style={{ margin: "40px 0" }} />

            {/* ========================= */}
            {/* Payroll */}
            {/* ========================= */}
            <h3>Record Payroll Payment</h3>
            <div className="vertical-form">
                <input
                    type="date"
                    value={payroll.date}
                    onChange={e => setPayroll({ ...payroll, date: e.target.value })}
                />

                <select
                    value={payroll.teacher}
                    onChange={e => setPayroll({ ...payroll, teacher: e.target.value })}
                >
                    <option value="">Teacher</option>
                    {teachers.map(t => (
                        <option key={t._id} value={t.name}>{t.name}</option>
                    ))}
                </select>

                <input
                    type="number"
                    step="0.01"
                    placeholder="Total Pay ($)"
                    value={payroll.totalPay}
                    onChange={e => setPayroll({ ...payroll, totalPay: e.target.value })}
                />

                <button className="submit-btn" onClick={submitPayroll}>
                    Record Payroll
                </button>
            </div>

            {status && <p className="status-msg">{status}</p>}
        </div>
    );
};

export default AdminFinancialEntry;
